import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { createKunde, previewKunde, type KundeIssue, type KundePreview } from './index';

interface Props {
  address: string;
  onCreated: (p: KundePreview) => void;
  onError: (msg: string) => void;
}

/** Zeichen, die im Feld unsichtbar sind, aber die Regel zerstoeren. */
const CONFUSABLES = 'аеорсухіѕјАВЕКМНОРСТУХІ';

/**
 * Zeigt die Adresse so, wie sie wirklich ist: Leerzeichen als ␣, kyrillische
 * Sichtdubletten rot. Ohne das sieht "е0923118@gmail.com" exakt aus wie
 * "e0923118@gmail.com" — genau dieser Fall lag monatelang unentdeckt in der Basis.
 */
function RevealedText({ value }: { value: string }) {
  const chars = Array.from(value);
  const suspicious = chars.some((c) => c === ' ' || CONFUSABLES.includes(c));
  if (!suspicious) return null;

  return (
    <Box sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
      {chars.map((ch, i) => {
        if (ch === ' ') {
          return (
            <Box key={i} component="span" sx={{ bgcolor: 'warning.light', px: '1px' }}>
              ␣
            </Box>
          );
        }
        if (CONFUSABLES.includes(ch)) {
          return (
            <Tooltip key={i} title={`кириллическая «${ch}»`}>
              <Box
                component="span"
                sx={{
                  bgcolor: 'error.light',
                  color: 'error.contrastText',
                  textDecoration: 'underline wavy',
                }}
              >
                {ch}
              </Box>
            </Tooltip>
          );
        }
        return <span key={i}>{ch}</span>;
      })}
    </Box>
  );
}

export default function KundeForm({ address, onCreated, onError }: Props) {
  const { t } = useTranslation();
  const [vorname, setVorname] = useState('');
  const [nachname, setNachname] = useState('');
  const [email, setEmail] = useState('');
  const [alias, setAlias] = useState('');
  // Sobald der Alias von Hand angefasst wurde, nicht mehr ueberschreiben.
  const [aliasTouched, setAliasTouched] = useState(false);
  const [checked, setChecked] = useState<KundePreview | null>(null);

  const previewMut = useMutation({
    mutationFn: (payload: { vorname: string; nachname: string; email: string; alias?: string | null }) =>
      previewKunde(address, payload),
  });

  const createMut = useMutation({
    mutationFn: () => createKunde(address, { vorname, nachname, email, alias: aliasTouched ? alias : null }),
    onSuccess: (p) => {
      onCreated(p);
      setVorname('');
      setNachname('');
      setEmail('');
      setAlias('');
      setAliasTouched(false);
      setChecked(null);
    },
    onError: (e: unknown) => onError(String(e)),
  });

  // Jede Aenderung entwertet eine frueher bestandene Pruefung — sonst speichert
  // man den geprueften Stand nicht, sondern den danach getippten.
  useEffect(() => {
    setChecked(null);
  }, [vorname, nachname, email, alias]);

  async function runPreview(forCheck: boolean) {
    const p = await previewMut.mutateAsync({
      vorname,
      nachname,
      email,
      alias: aliasTouched ? alias : null,
    });
    if (!aliasTouched && p.alias) setAlias(p.alias);
    if (forCheck) setChecked(p);
    return p;
  }

  // Alias vorschlagen, sobald Vor-/Nachname fertig getippt sind.
  function suggestAlias() {
    if (!vorname.trim() || !nachname.trim() || aliasTouched) return;
    previewMut
      .mutateAsync({ vorname, nachname, email, alias: null })
      .then((p) => {
        if (p.alias) setAlias(p.alias);
      })
      .catch(() => undefined);
  }

  const issues = checked?.issues ?? [];
  const blocking = issues.filter((i) => i.blocking);
  const hints = issues.filter((i) => !i.blocking);
  const canSave = !!checked?.ok && !createMut.isPending;

  const complete = useMemo(
    () => vorname.trim() !== '' && nachname.trim() !== '' && email.trim() !== '',
    [vorname, nachname, email]
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h3" sx={{ mb: 0.5 }}>
        {t('mail.kunde.title')}
      </Typography>
      <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
        {t('mail.kunde.hint')}
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        <Stack spacing={2} sx={{ flex: 1, minWidth: 260 }}>
          <TextField
            size="small"
            label={t('mail.kunde.vorname')}
            value={vorname}
            onChange={(e) => setVorname(e.target.value)}
            onBlur={suggestAlias}
            fullWidth
          />
          <TextField
            size="small"
            label={t('mail.kunde.nachname')}
            value={nachname}
            onChange={(e) => setNachname(e.target.value)}
            onBlur={suggestAlias}
            fullWidth
          />
          <Box>
            <TextField
              size="small"
              label={t('mail.kunde.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="kunde@gmail.com"
              fullWidth
            />
            <RevealedText value={email} />
          </Box>
        </Stack>

        <Stack spacing={2} sx={{ flex: 1, minWidth: 260 }}>
          <Box>
            <TextField
              size="small"
              label={t('mail.kunde.alias')}
              value={alias}
              onChange={(e) => {
                setAlias(e.target.value);
                setAliasTouched(true);
              }}
              placeholder={t('mail.kunde.aliasPlaceholder')}
              fullWidth
              InputProps={{
                endAdornment: aliasTouched ? (
                  <Tooltip title={t('mail.kunde.regenerate')}>
                    <Button
                      size="small"
                      onClick={() => {
                        setAliasTouched(false);
                        previewMut
                          .mutateAsync({ vorname, nachname, email, alias: null })
                          .then((p) => setAlias(p.alias))
                          .catch(() => undefined);
                      }}
                    >
                      <RefreshIcon fontSize="small" />
                    </Button>
                  </Tooltip>
                ) : null,
              }}
            />
            <RevealedText value={alias} />
            <Typography variant="caption" color="textSecondary">
              {aliasTouched ? t('mail.kunde.aliasManual') : t('mail.kunde.aliasAuto')}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label={`${t('mail.kunde.folder')}: INBOX.KUNDEN`} />
            <Chip size="small" label={`${t('mail.kunde.field')}: to`} />
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => runPreview(true)}
              disabled={!complete || previewMut.isPending}
              startIcon={previewMut.isPending ? <CircularProgress size={16} /> : undefined}
            >
              {t('mail.kunde.check')}
            </Button>
            <Button
              variant="contained"
              onClick={() => createMut.mutate()}
              disabled={!canSave}
              startIcon={checked?.ok ? <CheckCircleIcon /> : undefined}
            >
              {t('mail.kunde.save')}
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {blocking.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Stack spacing={0.5}>
            {blocking.map((i: KundeIssue, n: number) => (
              <span key={n}>{i.message}</span>
            ))}
          </Stack>
        </Alert>
      )}
      {blocking.length === 0 && hints.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Stack spacing={0.5}>
            {hints.map((i: KundeIssue, n: number) => (
              <span key={n}>{i.message}</span>
            ))}
          </Stack>
        </Alert>
      )}
      {checked?.ok && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {t('mail.kunde.ready', { alias: checked.alias, email: checked.email })}
        </Alert>
      )}
    </Paper>
  );
}
